import React, { useEffect, useRef } from "react";
import clsx from "clsx";

type ToastType = "success" | "error" | "warning" | "info";

interface ToastProps {
  message: string;
  type: ToastType;
  onClose: () => void;
  duration?: number;
  /** Optional action button */
  action?: {
    label: string;
    onClick: () => void;
  };
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose, duration = 5000, action }) => {
  const toastRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }
  };

  const typeClasses = {
    success: "bg-success",
    error: "bg-error",
    warning: "bg-warning",
    info: "bg-info",
  };

  // Screen reader announcement priority based on type
  const ariaLive = type === "error" ? "assertive" : "polite";
  const role = type === "error" ? "alert" : "status";

  const icons = {
    success: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    ),
    error: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clipRule="evenodd"
        />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
    ),
    info: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
    ),
  };

  const typeLabels = {
    success: "Success",
    error: "Error",
    warning: "Warning",
    info: "Information",
  };

  return (
    <div
      ref={toastRef}
      role={role}
      aria-live={ariaLive}
      aria-atomic="true"
      onKeyDown={handleKeyDown}
      className={clsx(
        "flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-white max-w-md border border-white/10",
        typeClasses[type],
        "animate-slide-in motion-reduce:animate-none",
        // Add focus styles for keyboard users
        "focus-within:ring-2 focus-within:ring-white/50"
      )}
    >
      <div className="flex-shrink-0" aria-hidden="true">
        {icons[type]}
      </div>
      <div className="flex-1 min-w-0">
        {/* Screen reader only label for context */}
        <span className="sr-only">{typeLabels[type]}: </span>
        <p className="text-sm font-medium">{message}</p>
      </div>
      {action && (
        <button
          onClick={action.onClick}
          className="flex-shrink-0 text-sm font-semibold underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-white/50 rounded px-1"
        >
          {action.label}
        </button>
      )}
      <button
        ref={closeButtonRef}
        onClick={onClose}
        className="flex-shrink-0 hover:opacity-80 transition-opacity p-1 rounded focus:outline-none focus:ring-2 focus:ring-white/50 min-w-[32px] min-h-[32px] flex items-center justify-center"
        aria-label={`Dismiss ${typeLabels[type].toLowerCase()} notification`}
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
};

export default Toast;
